import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  CartService,
  DataService,
  DocumentService,
  PharmacyService
} from '../../services';
import { Drug, FilterDetails, User, Search, QuantityDetails, CartQuantityItem } from '../../models';
import Swal from 'sweetalert2';
import { first, mergeMap, tap } from 'rxjs/operators';
import { is } from 'date-fns/locale';

@Component({
  selector: 'app-drugs-template',
  templateUrl: './drugs-template.component.html',
  styleUrls: ['./drugs-template.component.scss']
})

export class DrugsTemplateComponent implements OnInit {
  selectedCountries: string[];
  itemsPerPage = 9;
  page: number = 1;
  filterIcons: FilterDetails[] = [];
  drugs: Drug[] = [];
  user: User;
  cartItems: Drug[] = [];
  categoryNames: string[] = [];
  isSelectedMedicalTest: boolean = false;
  isResponsive: boolean = true;
  stateOfFilter: boolean[] = [];
  slickCarouselConfig: any;
  selectedQuantities: { [drugId: string]: any } = {};
  filteredDrugs: Drug[] = [];
  countryFilters: string[] = [];
  searchCriteria: Search;
  searchInput: string = '';
  isRemoveCategoryFilters: boolean;
  isUpdateDrugQuantityType: { [drugId: number]: boolean } = {};
  productQuantities: CartQuantityItem[] = [];

  constructor(
    private router: Router,
    private cartService: CartService,
    private dataService: DataService,
    private documentService: DocumentService,
    private pharmacyService: PharmacyService
  ) {}

  ngOnInit() {
    this.slickCarouselConfig = {
      ...this.documentService.getslickCarouselConfig(),
      autoplay: true,
      autoplaySpeed: 3000,
      slidesToScroll: 3
    };
    this.filterIcons = this.dataService.getPharmacyFilterDetails();
    this.cartService.getCartProducts().subscribe(cartItems => {
      this.cartItems = (!cartItems || cartItems.length === 0) ? 
        JSON.parse(localStorage.getItem('cartItems') || '[]') : cartItems as Drug[];
    });
    this.dataService.getSearchCriteria('drugs').subscribe(searchCriteria => {
      if ((searchCriteria && searchCriteria?.searchInput) || (searchCriteria && searchCriteria?.category)) {
        this.searchCriteria = searchCriteria;
        this.filterDrugs(this.searchCriteria, true); 
      } else {
        this.populateDrugs();
      }
    }); 
    this.dataService.getLocalSelectedCountriesFilters().subscribe(countries => {
      const isCountries = Array.isArray(countries) && countries.length > 0;
      const isSearchInput = this.searchCriteria?.searchInput?.trim();
      const isCategory = !!this.searchCriteria?.category;
      if (isCountries && (isSearchInput || isCategory) || (!isCountries && (isSearchInput || isCategory))) {
        this.selectedCountries = countries;
        this.filterDrugsByCountries();
      } else if (!isCountries && !isSearchInput) {
        this.isRemoveCategoryFilters = false;
        return;
      }
    });
    this.adjustItemsPerPage(window.innerWidth);
  }

  populateDrugs() {
    this.stateOfFilter = [];
    this.isRemoveCategoryFilters = false;
    this.pharmacyService.getDrugs().pipe(first()).subscribe(drugs => {
      this.drugs = drugs;
      this.dataService.saveAllLocalDrugs(this.drugs);
      this.initializeSelectedQuantities();
    });
  }

  onQuantityTypeChange(drugId: number, quantityType: any) {
    this.selectedQuantities[drugId] = quantityType;
    const cartItem = this.cartItems.find(item => item.id === drugId);
    const productQuantity = this.productQuantities.find(product => product.productId === drugId);
    this.isUpdateDrugQuantityType[drugId] = !!(cartItem && productQuantity?.quantity_type !== quantityType.name);
  }

  initializeSelectedQuantities(): void {
    this.selectedQuantities = {};
    this.productQuantities = JSON.parse(localStorage.getItem('cartItemQuantities') || '[]');
    for (const drug of this.drugs) {
      if (Array.isArray(drug.quantity_types) && drug.quantity_types.length > 0) {    
        const existingCartItem = this.productQuantities.find(product => product.productId === drug.id);
        if (existingCartItem) {
          const matchedType = drug.quantity_types.find(quantity => quantity.name === existingCartItem.quantity_type);
          this.selectedQuantities[drug.id] = matchedType || drug.quantity_types[0];
        } else {
          this.selectedQuantities[drug.id] = drug.quantity_types[0];
        }
      } else {
        this.selectedQuantities[drug.id] = null;
      }
    }
  }

  addToCart(drug: Drug, selectedType: QuantityDetails) {
    const cartHasMedicalTest = this.cartItems.some(item => !('country_of_origin' in item));
      if ( cartHasMedicalTest) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'You can only add either a medical test or a drug to the cart at a time.',
          confirmButtonColor: '#f37b7b',
        });
        return;
      }
    this.dataService.addProductToCart(drug, selectedType);
    this.refreshCartState(drug.id, selectedType);
  }

  refreshCartState(drugId: number, selectedType: QuantityDetails): void {
    const { cartItems, quantities } = this.dataService.refreshCartState();
    this.cartItems = cartItems.filter((item): item is Drug => (item as Drug).country_of_origin !== undefined);
    this.productQuantities = quantities;
    this.isUpdateDrugQuantityType[drugId] = false;
  }

  filterDrugs(searchCriteria: Search, updateCountries?: boolean, index?: number) {
    this.dataService.filterDrugs(searchCriteria, index)
      .subscribe(response => {
        this.drugs = response;
        if (updateCountries) {
          this.isRemoveCategoryFilters = true;
          const uniqueCountriesMap = new Map<string, string>();
          for (const country of this.drugs.map(drug => drug.country_of_origin)) {
            if (typeof country === 'string') {
              const lower = country.toLowerCase();
              if (!uniqueCountriesMap.has(lower)) {
                uniqueCountriesMap.set(lower, country);
              }
            }
          }
          this.countryFilters = Array.from(uniqueCountriesMap.values());
          this.dataService.saveLocalCountryFilters(this.countryFilters);
        }
        this.initializeSelectedQuantities();
      });
  }
 
  filterDrugsByCountries() {
    this.dataService.getSearchCriteria('drugs').subscribe(searchCriteria => {
      if (searchCriteria) {
        this.searchCriteria = { ...searchCriteria, ['country_of_origin[]']: this.selectedCountries } as any;
        this.filterDrugs(this.searchCriteria, false);
      }
    });
  }

  goToDrugDetails(drug: Drug) {
    this.dataService.saveLocalDataToView(drug);
    this.router.navigate(['single-drug-details']);
  }

  adjustItemsPerPage(width: number) {
    this.itemsPerPage = (width >= 768 && width <= 1199) ? 8 : 9; // Between 768px and 1200px → Show 8 items to balance the white space
  }
}
